// src/components/SensorDashboard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, subDays, subMonths, subYears, differenceInMinutes } from 'date-fns';
import { getSensorType, processSensorReadings } from '../services/sensorTypesService';
import { fetchSensors, fetchSensorDataa, checkSensorExists } from '../services/apiService';
import { saveToStorage, getFromStorage } from '../services/storageService';
import { formatItalianTime, isSensorInactive, parseTimestamp } from '../utils/timeUtils';
import MetricSelector from './common/MetricSelector';
import { UI_CONFIG } from '../config/AppConfig';
// Componenti
import ErrorBoundary from './ErrorBoundary';
import DashboardControls from './common/DashboardControls';
import SensorSelector from './common/SensorSelector';
import EditModePanel from './common/EditModePanel';
import SensorCard from './sensors/SensorCard';

const SensorDashboard = () => {
  // Stati principali
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('day');
  const [chartData, setChartData] = useState({});
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [displayedMetrics, setDisplayedMetrics] = useState([
    'temperatura', 
    'umidita', 
    'pressure', 
    'windSpeed', 
    'lightIntensity',
    'dewPoint'
  ]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sensorStats, setSensorStats] = useState({});
  const [availableSensors, setAvailableSensors] = useState([]);
  const [dataAggregation, setDataAggregation] = useState('none');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [dataLimit, setDataLimit] = useState(500);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [loadingSensors, setLoadingSensors] = useState({});
  
  // Stati per le funzionalità di personalizzazione
  const [sensorNicknames, setSensorNicknames] = useState(
    getFromStorage('sensorNicknames', {})
  );
  const [sensorOrder, setSensorOrder] = useState(
    getFromStorage('sensorOrder', [])
  );
  const [editMode, setEditMode] = useState(false);
  
  // Riferimento per lo scroll automatico
  const mainContainerRef = useRef(null);

  // Funzione per ottenere il nome visualizzato del sensore
  const getSensorDisplayName = (sensorName) => {
    return sensorNicknames[sensorName] || sensorName;
  };
  useEffect(() => {
    if (availableSensors.length > 0 && !loading) {
      const savedSensors = getFromStorage('selectedSensors', []);
      
      if (savedSensors.length > 0) {
        // Filtra solo i sensori che esistono ancora
        const validSavedSensors = savedSensors.filter(s => availableSensors.includes(s));
        
        if (validSavedSensors.length > 0) {
          setSelectedSensors(validSavedSensors);
          
          // Carica i dati solo per i sensori selezionati
          fetchSensorsSequentially(validSavedSensors);
        } else {
          // Se nessun sensore salvato è valido, seleziona tutti i sensori disponibili
          setSelectedSensors(availableSensors);
          
          // Salva la nuova selezione
          saveToStorage('selectedSensors', availableSensors);
          
          // Carica i dati per tutti i sensori disponibili
          fetchSensorsSequentially(availableSensors);
        }
      } else {
        // Se non ci sono sensori salvati, seleziona tutti i sensori disponibili
        setSelectedSensors(availableSensors);
        
        // Salva la selezione iniziale
        saveToStorage('selectedSensors', availableSensors);
        
        // Carica i dati per tutti i sensori disponibili
        fetchSensorsSequentially(availableSensors);
      }
    }
  }, [availableSensors, loading]);

  // Effetto per salvare la selezione dei sensori nel localStorage
  useEffect(() => {
    if (selectedSensors.length > 0) {
      saveToStorage('selectedSensors', selectedSensors);
    }
  }, [selectedSensors]);

  // Carica la selezione dei sensori dal localStorage all'avvio
  useEffect(() => {
    const savedSensors = getFromStorage('selectedSensors', []);
    if (savedSensors.length > 0) {
      setSelectedSensors(savedSensors);
    }
  }, []);

  // Salva i nickname dei sensori nel localStorage quando cambiano
  useEffect(() => {
    saveToStorage('sensorNicknames', sensorNicknames);
  }, [sensorNicknames]);

  // Salva l'ordine dei sensori nel localStorage quando cambia
  useEffect(() => {
    saveToStorage('sensorOrder', sensorOrder);
  }, [sensorOrder]);


// Modifica alla funzione calculateSensorStats in SensorDashboard.js
const calculateSensorStats = useCallback((sensorName, readings) => {
  if (!readings || readings.length === 0) {
    // Set default stats for empty data
    setSensorStats(prevStats => ({
      ...prevStats,
      [sensorName]: createDefaultSensorStats(sensorName)
    }));
    return;
  }
  // Aggiungi questo codice in un punto appropriato di SensorDashboard.js,
// ad esempio dopo aver ricevuto i dati dal server ma prima di elaborarli

// Funzione helper per esaminare i dati
const debugSensorData = (sensorName, readings) => {
  if (!readings || !readings.length) {
    console.log(`Nessun dato per il sensore ${sensorName}`);
    return;
  }
  
  // Prendiamo l'ultima lettura
  const lastReading = readings[readings.length - 1];
  console.log(`===== DEBUG SENSORE ${sensorName} =====`);
  console.log('Timestamp originale:', lastReading.dateString);
  console.log('Ora attuale:', new Date().toISOString());
  
  // Calcoliamo manualmente l'inattività
  const lastTimestamp = new Date(lastReading.dateString);
  const now = new Date();
  const diffMs = now.getTime() - lastTimestamp.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  
  console.log('Timestamp convertito:', lastTimestamp.toISOString());
  console.log('Differenza in ms:', diffMs);
  console.log('Differenza in minuti:', diffMinutes);
  console.log('Sensore inattivo?', diffMinutes > 30);
  console.log('=====================================');
};

// Poi chiama questa funzione dopo aver ricevuto i dati
// ad esempio in fetchSensorData:

const processedReadings = readings.length > 0 ? 
  processSensorReadings(sensorName, readings) : [];

// Aggiungi questa riga
debugSensorData(sensorName, processedReadings);

  // Calcola timestamp di 12 ore fa per filtrare i dati per min/max
  const twelveHoursAgo = new Date();
  twelveHoursAgo.setHours(twelveHoursAgo.getHours() - 12);
  const twelveHoursAgoTimestamp = twelveHoursAgo.getTime();
  
  // Filtriamo le letture dalle ultime 12 ore per calcolo min/max
  const recentReadings = readings.filter(reading => reading.timestamp >= twelveHoursAgoTimestamp);
  
  // Usa tutte le letture se non ci sono dati recenti nelle ultime 12 ore
  const dataForMinMax = recentReadings.length > 0 ? recentReadings : readings;
  
  const sensorType = getSensorType(sensorName);
  
  if (sensorType === 'level') {
    // Statistics for level sensor
    const livelloValues = dataForMinMax.map(d => parseFloat(d.livello) || 0);
    
    const validLivelloValues = livelloValues.filter(val => !isNaN(val) && val !== null);
    const livelloMin = validLivelloValues.length > 0 ? Math.min(...validLivelloValues) : 0;
    const livelloMax = validLivelloValues.length > 0 ? Math.max(...validLivelloValues) : 0;
    
    // Trova gli indici nei dati filtrati
    const livelloMinIndex = livelloValues.indexOf(livelloMin);
    const livelloMaxIndex = livelloValues.indexOf(livelloMax);
    
    const livelloMinTime = livelloMinIndex >= 0 ? dataForMinMax[livelloMinIndex]?.timeValue || '00:00' : '00:00';
    const livelloMaxTime = livelloMaxIndex >= 0 ? dataForMinMax[livelloMaxIndex]?.timeValue || '00:00' : '00:00';
    
    // Per il valore corrente e altre statistiche, usiamo sempre l'ultima lettura
    const lastValue = readings[readings.length - 1];
    
    const avgLivello = validLivelloValues.length > 0 
      ? (validLivelloValues.reduce((acc, val) => acc + val, 0) / validLivelloValues.length).toFixed(1) 
      : 0;
    
      const isActive = lastValue ? !isSensorInactive(lastValue.dateString, 30) : false;

    // Update stats for this sensor
    setSensorStats(prevStats => ({
      ...prevStats,
      [sensorName]: {
        sensorType: 'level',
        currentLivello: lastValue ? parseFloat(lastValue.livello) || 0 : 0,
        livelloMin,
        livelloMax,
        livelloMinTime,
        livelloMaxTime,
        avgLivello,
        readings: readings.length,
        lastUpdateTime: lastValue ? lastValue.date : null,
        isActive
      }
    }));
  }
  else if (sensorType === 'weather') {
    // Statistics for weather station - basate sulle ultime 12 ore per min/max
    const tempValues = dataForMinMax.map(d => parseFloat(d.temperatura) || 0);
    const umidValues = dataForMinMax.map(d => parseFloat(d.umidita) || 0);
    
    const validTempValues = tempValues.filter(val => !isNaN(val) && val !== null);
    const tempMin = validTempValues.length > 0 ? Math.min(...validTempValues) : 0;
    const tempMax = validTempValues.length > 0 ? Math.max(...validTempValues) : 0;
    
    const validUmidValues = umidValues.filter(val => !isNaN(val) && val !== null && val > 0);
    const hasUmidita = validUmidValues.length > 0;
    let umidMin = 0, umidMax = 0;
    
    if (hasUmidita) {
      umidMin = Math.min(...validUmidValues);
      umidMax = Math.max(...validUmidValues);
    }
    
    // Trova gli indici nei dati filtrati (ultime 12 ore)
    const tempMinIndex = tempValues.indexOf(tempMin);
    const tempMaxIndex = tempValues.indexOf(tempMax);
    
    const tempMinTime = tempMinIndex >= 0 ? dataForMinMax[tempMinIndex]?.timeValue || '00:00' : '00:00';
    const tempMaxTime = tempMaxIndex >= 0 ? dataForMinMax[tempMaxIndex]?.timeValue || '00:00' : '00:00';
    
    let umidMinTime = '00:00', umidMaxTime = '00:00';
    
    if (hasUmidita) {
      const umidMinIndex = umidValues.indexOf(umidMin);
      const umidMaxIndex = umidValues.indexOf(umidMax);
      
      umidMinTime = umidMinIndex >= 0 ? dataForMinMax[umidMinIndex]?.timeValue || '00:00' : '00:00';
      umidMaxTime = umidMaxIndex >= 0 ? dataForMinMax[umidMaxIndex]?.timeValue || '00:00' : '00:00';
    }
    
    // Usa l'ultimo valore dalle letture complete
    const lastValue = readings[readings.length - 1];
    
    // Per media e altre statistiche usiamo tutte le letture disponibili
    const allTempValues = readings.map(d => parseFloat(d.temperatura) || 0)
      .filter(val => !isNaN(val) && val !== null);
    
    const allUmidValues = readings.map(d => parseFloat(d.umidita) || 0)
      .filter(val => !isNaN(val) && val !== null && val > 0);
    
    const avgTemp = allTempValues.length > 0 
      ? (allTempValues.reduce((acc, val) => acc + val, 0) / allTempValues.length).toFixed(1) 
      : 0;
      
    const avgUmid = allUmidValues.length > 0 
      ? (allUmidValues.reduce((acc, val) => acc + val, 0) / allUmidValues.length).toFixed(1) 
      : 0;
    
      const isActive = lastValue ? !isSensorInactive(lastValue.dateString, 30) : false;
    
    // Update stats for this sensor
    setSensorStats(prevStats => ({
      ...prevStats,
      [sensorName]: {
        sensorType: 'weather',
        currentTemp: lastValue ? parseFloat(lastValue.temperatura) || 0 : 0,
        currentUmid: lastValue && hasUmidita ? parseFloat(lastValue.umidita) || 0 : 0,
        hasUmidita,
        tempMin,
        tempMax,
        umidMin,
        umidMax,
        tempMinTime,
        tempMaxTime,
        umidMinTime,
        umidMaxTime,
        avgTemp,
        avgUmid,
        lightIntensity: lastValue ? parseFloat(lastValue.lightIntensity) || 0 : 0,
        uvIndex: lastValue ? parseFloat(lastValue.uvIndex) || 0 : 0,
        windSpeed: lastValue ? parseFloat(lastValue.windSpeed) || 0 : 0,
        windDirection: lastValue ? parseFloat(lastValue.windDirection) || 0 : 0,
        rainGauge: lastValue ? parseFloat(lastValue.rainGauge) || 0 : 0,
        pressure: lastValue ? parseFloat(lastValue.pressure) || 0 : 0,
        readings: readings.length,
        lastUpdateTime: lastValue ? lastValue.date : null,
        isActive
      }
    }));
  }
  else {
    // Statistics for regular sensors (temperature/humidity) - basate sulle ultime 12 ore per min/max
    const tempValues = dataForMinMax.map(d => parseFloat(d.temperatura) || 0);
    const umidValues = dataForMinMax.map(d => parseFloat(d.umidita) || 0);
    
    const validTempValues = tempValues.filter(val => !isNaN(val) && val !== null);
    const tempMin = validTempValues.length > 0 ? Math.min(...validTempValues) : 0;
    const tempMax = validTempValues.length > 0 ? Math.max(...validTempValues) : 0;
    
    const validUmidValues = umidValues.filter(val => !isNaN(val) && val !== null && val > 0);
    const hasUmidita = validUmidValues.length > 0;
    let umidMin = 0, umidMax = 0;
    
    if (hasUmidita) {
      umidMin = Math.min(...validUmidValues);
      umidMax = Math.max(...validUmidValues);
    }
    
    const tempMinIndex = tempValues.indexOf(tempMin);
    const tempMaxIndex = tempValues.indexOf(tempMax);
    
    const tempMinTime = tempMinIndex >= 0 ? dataForMinMax[tempMinIndex]?.timeValue || '00:00' : '00:00';
    const tempMaxTime = tempMaxIndex >= 0 ? dataForMinMax[tempMaxIndex]?.timeValue || '00:00' : '00:00';
    
    let umidMinTime = '00:00', umidMaxTime = '00:00';
    
    if (hasUmidita) {
      const umidMinIndex = umidValues.indexOf(umidMin);
      const umidMaxIndex = umidValues.indexOf(umidMax);
      
      umidMinTime = umidMinIndex >= 0 ? dataForMinMax[umidMinIndex]?.timeValue || '00:00' : '00:00';
      umidMaxTime = umidMaxIndex >= 0 ? dataForMinMax[umidMaxIndex]?.timeValue || '00:00' : '00:00';
    }
    
    // Usa l'ultimo valore dalle letture complete
    const lastValue = readings[readings.length - 1];
    
    // Per media e altre statistiche usiamo tutte le letture disponibili
    const allTempValues = readings.map(d => parseFloat(d.temperatura) || 0)
      .filter(val => !isNaN(val) && val !== null);
    
    const allUmidValues = readings.map(d => parseFloat(d.umidita) || 0)
      .filter(val => !isNaN(val) && val !== null && val > 0);
    
    const avgTemp = allTempValues.length > 0 
      ? (allTempValues.reduce((acc, val) => acc + val, 0) / allTempValues.length).toFixed(1) 
      : 0;
      
    const avgUmid = allUmidValues.length > 0 
      ? (allUmidValues.reduce((acc, val) => acc + val, 0) / allUmidValues.length).toFixed(1) 
      : 0;
    
      const isActive = lastValue ? !isSensorInactive(lastValue.dateString, 30) : false;
    // Update stats for this sensor
    setSensorStats(prevStats => ({
      ...prevStats,
      [sensorName]: {
        sensorType: 'regular',
        currentTemp: lastValue ? parseFloat(lastValue.temperatura) || 0 : 0,
        currentUmid: lastValue && hasUmidita ? parseFloat(lastValue.umidita) || 0 : 0,
        hasUmidita,
        tempMin,
        tempMax,
        umidMin,
        umidMax,
        tempMinTime,
        tempMaxTime,
        umidMinTime,
        umidMaxTime,
        avgTemp,
        avgUmid,
        readings: readings.length,
        lastUpdateTime: lastValue ? lastValue.date : null,
        isActive
      }
    }));
  }
  
  // Update the last general update if this is more recent
  const lastValue = readings[readings.length - 1];
  if (lastValue && (!lastUpdate || lastValue.date > lastUpdate)) {
    setLastUpdate(lastValue.date);
  }
}, [lastUpdate]);

// Aggiungi questa funzione per caricare i sensori in modo sequenziale
const fetchSensorsSequentially = async (sensorList) => {
    setIsLoading(true);
    
    // Crea una copia dell'array per non modificare l'originale
    const sensorsToLoad = [...sensorList];
    
    // Funzione ricorsiva per caricare un sensore alla volta
    const loadNextSensor = async () => {
      if (sensorsToLoad.length === 0) {
        setIsLoading(false);
        return;
      }
      
      // Prendi il prossimo sensore dalla lista
      const sensor = sensorsToLoad.shift();
      
      try {
        // Imposta lo stato di caricamento per questo sensore
        setLoadingSensors(prev => ({
          ...prev,
          [sensor]: true
        }));
        
        // Costruisci i parametri per la query
        const options = {
          limit: dataLimit,
          aggregation: dataAggregation
        };
        
        // Aggiungi parametri temporali
        if (timeRange === 'custom') {
          options.startDate = `${customDateRange.startDate} 00:00:00`;
          options.endDate = `${customDateRange.endDate} 23:59:59`;
        } else if (timeRange === 'day') {
          options.startDate = format(subDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
        } else if (timeRange === 'week') {
          options.startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd HH:mm:ss');
        } else if (timeRange === 'month') {
          options.startDate = format(subMonths(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
        } else if (timeRange === 'year') {
          options.startDate = format(subYears(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
        }
        
        // Ottieni i dati dall'API
        const data = await fetchSensorDataa(sensor, options);
        
        // Verifica che la risposta sia valida
        if (!data) {
          console.warn(`Nessun dato disponibile per il sensore ${sensor}`);
          
          // Aggiorna lo stato con dati vuoti
          setChartData(prevData => ({
            ...prevData,
            [sensor]: []
          }));
          
          setAllData(prevData => ({
            ...prevData,
            [sensor]: []
          }));
          
          // Imposta statistiche di default
          setSensorStats(prevStats => ({
            ...prevStats,
            [sensor]: createDefaultSensorStats(sensor)
          }));
          
          // Reimposta lo stato di caricamento
          setLoadingSensors(prev => ({
            ...prev,
            [sensor]: false
          }));
          
          // Passa al prossimo sensore
          await loadNextSensor();
          return;
        }
        
        // Assicurati che data sia un array
        const readings = Array.isArray(data) ? data : [];
        
        // Elabora i dati in base al tipo di sensore
        const processedReadings = readings.length > 0 ? 
          processSensorReadings(sensor, readings) : [];
        
        // Se abbiamo letture, ordinale e calcola le statistiche
        if (processedReadings.length > 0) {
          // Ordina i dati
          processedReadings.sort((a, b) => a.date - b.date);
          
          // Calcola le statistiche
          calculateSensorStats(sensor, processedReadings);
          
          // Aggiorna i dati del grafico
          setChartData(prevData => ({
            ...prevData,
            [sensor]: processedReadings
          }));
          
          setAllData(prevData => ({
            ...prevData,
            [sensor]: processedReadings
          }));
        } else {
          // Gestisci caso dati vuoti
          setChartData(prevData => ({
            ...prevData,
            [sensor]: []
          }));
          
          setAllData(prevData => ({
            ...prevData,
            [sensor]: []
          }));
          
          // Aggiorna statistiche con valori vuoti/default
          setSensorStats(prevStats => ({
            ...prevStats,
            [sensor]: createDefaultSensorStats(sensor)
          }));
        }
        
        // Reimposta lo stato di caricamento per questo sensore
        setLoadingSensors(prev => ({
          ...prev,
          [sensor]: false
        }));
        
      } catch (err) {
        console.error(`Error loading data for sensor ${sensor}:`, err);
        
        // Reimposta lo stato di caricamento in caso di errore
        setLoadingSensors(prev => ({
          ...prev,
          [sensor]: false
        }));
        
        // Imposta dati vuoti in caso di errore
        setChartData(prevData => ({
          ...prevData,
          [sensor]: []
        }));
        
        setAllData(prevData => ({
          ...prevData,
          [sensor]: []
        }));
        
        // Aggiorna statistiche con valori vuoti/default
        setSensorStats(prevStats => ({
          ...prevStats,
          [sensor]: createDefaultSensorStats(sensor)
        }));
      }
      
      // Piccola pausa per evitare di sovraccaricare il server
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Passa al prossimo sensore
      await loadNextSensor();
    };
    
    // Avvia il caricamento del primo sensore
    await loadNextSensor();
  };
  
const fetchSensorsList = useCallback(async () => {
  try {
    setIsLoading(true);
    
    // Ottieni la lista dei sensori
    let sensors = await fetchSensors();
    
    // Filtra i duplicati dalla risposta
    const uniqueSensorNames = new Set();
    const uniqueSensors = [];
    
    // Elabora ogni sensore dalla risposta
    for (const sensor of sensors) {
      const sensorName = typeof sensor === 'string' ? sensor : sensor.name;
      
      // Verifica che non sia già stato aggiunto
      if (!uniqueSensorNames.has(sensorName)) {
        uniqueSensorNames.add(sensorName);
        
        // Verifica che il sensore esista nel database
        const exists = await checkSensorExists(sensorName);
        if (exists) {
          uniqueSensors.push(sensorName);
        }
      }
    }
    
    setAvailableSensors(uniqueSensors);
    
    // Aggiorna l'ordine dei sensori se necessario
    if (sensorOrder.length === 0 && uniqueSensors.length > 0) {
      setSensorOrder(uniqueSensors);
    } else {
      // Aggiungi i nuovi sensori all'ordine
      const newSensors = uniqueSensors.filter(s => !sensorOrder.includes(s));
      if (newSensors.length > 0) {
        setSensorOrder([...sensorOrder, ...newSensors]);
      }
      
      // Rimuovi sensori che non esistono più
      const updatedOrder = sensorOrder.filter(s => uniqueSensors.includes(s));
      if (updatedOrder.length !== sensorOrder.length) {
        setSensorOrder(updatedOrder);
      }
    }
    
    // Non carichiamo qui i dati per i sensori selezionati, 
    // questo verrà gestito dall'useEffect che monitora availableSensors
    
    setLoading(false);
    setIsLoading(false);
  } catch (err) {
    console.error('Errore nel caricamento della lista dei sensori:', err);
    setError('Errore nel caricamento della lista dei sensori: ' + err.message);
    setLoading(false);
    setIsLoading(false);
  }
}, [sensorOrder]);

  const fetchSensorData = useCallback(async (sensorName) => {
    try {
      // Set loading state for this sensor
      setLoadingSensors(prev => ({
        ...prev,
        [sensorName]: true
      }));
      
      // Build query parameters
      const options = {
        limit: dataLimit,
        aggregation: dataAggregation
      };
      
      // Add time parameters
      if (timeRange === 'custom') {
        options.startDate = `${customDateRange.startDate} 00:00:00`;
        options.endDate = `${customDateRange.endDate} 23:59:59`;
      } else if (timeRange === 'day') {
        options.startDate = format(subDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
      } else if (timeRange === 'week') {
        options.startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd HH:mm:ss');
      } else if (timeRange === 'month') {
        options.startDate = format(subMonths(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
      } else if (timeRange === 'year') {
        options.startDate = format(subYears(new Date(), 1), 'yyyy-MM-dd HH:mm:ss');
      }
      
      // Retrieve data from the API
      const readings = await fetchSensorData(sensorName, options);
      
      // Process data based on sensor type - IMPORTANT: Only do this if there are readings
      const processedReadings = readings.length > 0 ? 
        processSensorReadings(sensorName, readings) : [];
      
      // If we have readings, sort them and calculate stats
      if (processedReadings.length > 0) {
        // Sort the data
        processedReadings.sort((a, b) => a.date - b.date);
        
        // Calculate statistics - but don't call recursively!
        calculateSensorStats(sensorName, processedReadings);
        
        // Update chart data
        setChartData(prevData => ({
          ...prevData,
          [sensorName]: processedReadings
        }));
        
        setAllData(prevData => ({
          ...prevData,
          [sensorName]: processedReadings
        }));
      } else {
        // Handle empty data case
        setChartData(prevData => ({
          ...prevData,
          [sensorName]: []
        }));
        
        setAllData(prevData => ({
          ...prevData,
          [sensorName]: []
        }));
        
        // Update stats with empty/default values
        setSensorStats(prevStats => ({
          ...prevStats,
          [sensorName]: createDefaultSensorStats(sensorName)
        }));
      }
      
      // Reset loading state for this sensor
      setLoadingSensors(prev => ({
        ...prev,
        [sensorName]: false
      }));
    } catch (err) {
      console.error(`Error loading data for sensor ${sensorName}:`, err);
      setLoadingSensors(prev => ({
        ...prev,
        [sensorName]: false
      }));
      
      // Set empty data on error
      setChartData(prevData => ({
        ...prevData,
        [sensorName]: []
      }));
      
      setAllData(prevData => ({
        ...prevData,
        [sensorName]: []
      }));
      
      // Update stats with empty/default values on error
      setSensorStats(prevStats => ({
        ...prevStats,
        [sensorName]: createDefaultSensorStats(sensorName)
      }));
    }
  }, [timeRange, customDateRange, dataLimit, dataAggregation, calculateSensorStats]);


  const createDefaultSensorStats = (sensorName) => {
    const sensorType = getSensorType(sensorName);
    
    if (sensorType === 'level') {
      return {
        sensorType: 'level',
        currentLivello: 0,
        livelloMin: 0,
        livelloMax: 0,
        livelloMinTime: '00:00',
        livelloMaxTime: '00:00',
        avgLivello: 0,
        readings: 0,
        lastUpdateTime: null,
        isActive: false
      };
    } else if (sensorType === 'weather') {
      return {
        sensorType: 'weather',
        currentTemp: 0,
        currentUmid: 0,
        hasUmidita: true,
        tempMin: 0,
        tempMax: 0,
        umidMin: 0,
        umidMax: 0,
        tempMinTime: '00:00',
        tempMaxTime: '00:00',
        umidMinTime: '00:00',
        umidMaxTime: '00:00',
        avgTemp: 0,
        avgUmid: 0,
        lightIntensity: 0,
        uvIndex: 0,
        windSpeed: 0,
        windDirection: 0,
        rainGauge: 0,
        pressure: 0,
        readings: 0,
        lastUpdateTime: null,
        isActive: false
      };
    } else {
      return {
        sensorType: 'regular',
        currentTemp: 0,
        currentUmid: 0,
        hasUmidita: true,
        tempMin: 0,
        tempMax: 0,
        umidMin: 0,
        umidMax: 0,
        tempMinTime: '00:00',
        tempMaxTime: '00:00',
        umidMinTime: '00:00',
        umidMaxTime: '00:00',
        avgTemp: 0,
        avgUmid: 0,
        readings: 0,
        lastUpdateTime: null,
        isActive: false
      };
    }
  };


  const loadSavedSensorSelection = () => {
    const savedSensors = getFromStorage('selectedSensors', []);
    if (savedSensors.length > 0) {
      // Verifica che i sensori salvati esistano ancora tra quelli disponibili
      const validSavedSensors = savedSensors.filter(s => availableSensors.includes(s));
      
      if (validSavedSensors.length > 0) {
        setSelectedSensors(validSavedSensors);
        return true; // Indica che sono stati caricati sensori salvati
      }
    }
    return false; // Indica che non sono stati caricati sensori salvati
  };

// Modifica la funzione fetchData per utilizzare il caricamento sequenziale
const fetchData = useCallback(() => {
    fetchSensorsList();
  }, [fetchSensorsList]);

  // Carica i dati all'avvio
  useEffect(() => {
    fetchSensorsList();
  }, [fetchSensorsList]);

  // Imposta l'aggiornamento automatico
  useEffect(() => {
    let intervalId;
    
    if (autoRefresh && refreshInterval > 0) {
      intervalId = setInterval(() => {
        fetchData();
      }, refreshInterval * 1000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, fetchData]);

// Modifica la funzione handleSensorChange per utilizzare il caricamento sequenziale
const handleSensorChange = (sensor) => {
  let updatedSelectedSensors;
  
  if (selectedSensors.includes(sensor)) {
    // Rimuovi il sensore dalla selezione
    updatedSelectedSensors = selectedSensors.filter(s => s !== sensor);
  } else {
    // Aggiungi il sensore alla selezione
    updatedSelectedSensors = [...selectedSensors, sensor];
  }
  
  // Aggiorna lo stato
  setSelectedSensors(updatedSelectedSensors);
  
  // Salva la selezione aggiornata in localStorage
  saveToStorage('selectedSensors', updatedSelectedSensors);
  
  // Se stiamo aggiungendo un sensore, carica i suoi dati
  if (!selectedSensors.includes(sensor)) {
    fetchSensorsSequentially([sensor]);
  }
};

  // Gestione cambio metrica
  const handleMetricChange = (metric) => {
    setDisplayedMetrics(prevMetrics => {
      if (prevMetrics.includes(metric)) {
        return prevMetrics.filter(m => m !== metric);
      } else {
        return [...prevMetrics, metric];
      }
    });
  };

  // Gestione refresh manuale
  const handleManualRefresh = () => {
    fetchData();
  };

  // Gestione espansione card
  const toggleExpandCard = (sensor) => {
    if (expandedCard === sensor) {
      setExpandedCard(null);
    } else {
      setExpandedCard(sensor);
      
      // Scroll alla card espansa dopo un breve delay per permettere il rendering
      setTimeout(() => {
        const element = document.getElementById(`sensor-card-${sensor.replace(/[^a-zA-Z0-9]/g, '-')}`);
        if (element && mainContainerRef.current) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Gestione modalità modifica
  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  // Ordina i sensori selezionati secondo l'ordine definito
  const sortedSelectedSensors = () => {
    return sensorOrder
      .filter(sensor => selectedSensors.includes(sensor))
      .filter(sensor => availableSensors.includes(sensor));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '16rem'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
        <span className="ms-2">Caricamento dati iniziale...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-danger p-3 bg-light rounded shadow-sm">
        <div className="fw-bold mb-2">Errore</div>
        {error}
        <div className="mt-3">
          <button className="btn btn-outline-primary btn-sm" onClick={handleManualRefresh}>
            Riprova
          </button>
        </div>
      </div>
    );
  }
  
  if (Object.keys(allData).length === 0) {
    return (
      <div className="text-center p-4">
        Nessun dato disponibile. 
        <button className="btn btn-outline-primary btn-sm ms-2" onClick={handleManualRefresh}>
          Riprova
        </button>
      </div>
    );
  }

  return (
    <div className="w-100" ref={mainContainerRef}>
      {/* Controlli principali */}
      <DashboardControls
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        dataAggregation={dataAggregation}
        setDataAggregation={setDataAggregation}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        refreshInterval={refreshInterval}
        setRefreshInterval={setRefreshInterval}
        isLoading={isLoading}
        handleManualRefresh={handleManualRefresh}
        editMode={editMode}
        toggleEditMode={toggleEditMode}
      />
      
      {/* Selettore sensori */}
      <div className="accordion mb-3" id="settingsAccordion">
  <SensorSelector
    availableSensors={availableSensors}
    selectedSensors={selectedSensors}
    handleSensorChange={handleSensorChange}
    getSensorDisplayName={getSensorDisplayName}
  />
  <MetricSelector
    displayedMetrics={displayedMetrics}
    handleMetricChange={handleMetricChange}
    availableMetrics={UI_CONFIG.METRICS}
    sensorType={selectedSensors.includes('Sensore5') ? 'weather' : 'regular'}
  />
</div>

      
      {/* Modalità modifica - mostrata solo quando editMode è attivo */}
      {editMode && (
        <EditModePanel
          sensorOrder={sensorOrder}
          sensorNicknames={sensorNicknames}
          toggleEditMode={toggleEditMode}
          setSensorNicknames={setSensorNicknames}
          setSensorOrder={setSensorOrder}
          getSensorDisplayName={getSensorDisplayName}
        />
      )}
      
      {/* Lista sensori */}
      <ErrorBoundary>
        <div className="row g-3">
          {sortedSelectedSensors().map(sensor => (
            <div 
              key={sensor} 
              className="col-12"
              id={`sensor-card-${sensor.replace(/[^a-zA-Z0-9]/g, '-')}`} 
            >
<SensorCard
  sensor={sensor}
  sensorStats={sensorStats[sensor]}
  chartData={chartData[sensor]}
  displayedMetrics={displayedMetrics}
  handleMetricChange={handleMetricChange}
  availableMetrics={UI_CONFIG.METRICS}
  getSensorDisplayName={getSensorDisplayName}
  isExpanded={expandedCard === sensor}
  toggleExpandCard={toggleExpandCard}
  formatItalianTime={formatItalianTime}
  dataAggregation={dataAggregation}
  isLoading={loadingSensors[sensor]}
/>
            </div>
          ))}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default SensorDashboard;